import { useCallback, useEffect, useState } from "react";
import { Checks } from "@phosphor-icons/react";
import { StatusPill } from "../components/StatusPill";
import { ExpensePanel } from "../components/ExpensePanel";
import { api, fmtMoney } from "../lib/api";
import { useT } from "../i18n";

export default function ApprovalsPage({ workspace }) {
  const { t } = useT();
  const [queue, setQueue] = useState([]);
  const [panel, setPanel] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/expenses", { params: { status: "submitted" } });
    setQueue(data);
    setLoaded(true);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{t("approvals_title")}</h1>
        <p className="text-sm text-slate-500">{t("approvals_sub")}</p>
      </div>

      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {[t("th_date"), t("th_vendor"), t("th_submittedBy"), t("th_category"), t("th_amount"), t("th_status")].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="approvals-table-body">
            {queue.map((e) => (
              <tr key={e.id} data-testid={`approval-row-${e.id}`} onClick={() => setPanel(e)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-150">
                <td className="px-3 py-2.5 font-mono text-xs text-slate-500 whitespace-nowrap">{e.date || "—"}</td>
                <td className="px-3 py-2.5 font-medium text-slate-900">{e.vendor || t("untitled")}</td>
                <td className="px-3 py-2.5 text-slate-500">{e.user_name}</td>
                <td className="px-3 py-2.5 text-slate-500">{t(`cat_${e.category}`)}</td>
                <td className="px-3 py-2.5 font-mono font-semibold text-slate-900 whitespace-nowrap">{fmtMoney(e.amount, e.currency)}</td>
                <td className="px-3 py-2.5"><StatusPill status={e.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loaded && queue.length === 0 && (
          <div className="py-20 flex flex-col items-center gap-3" data-testid="approvals-empty-state">
            <Checks size={40} className="text-slate-200" />
            <p className="font-heading font-semibold text-slate-900">{t("queueClear")}</p>
            <p className="text-sm text-slate-500">{t("queueClearSub")}</p>
          </div>
        )}
      </div>

      {panel && <ExpensePanel expense={panel} workspace={workspace} onClose={() => setPanel(null)} onChanged={load} />}
    </div>
  );
}
